import { FunctionComponent, ReactNode, useEffect, useRef } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import styled, { css } from 'styled-components'

import { mobileCSS } from '@measures/responsive'
import useBlickPlusOnboardingCurrentStep from '@hooks/useBlickPlusOnboardingCurrentStep'
import useBlickPlusOnboardingStepsABTest from '@hooks/useBlickPlusOnboardingStepsABTest'
import useBodyScroll from '@hooks/useBodyScroll'
import {
  OnboardingUtilities,
  Step,
} from '@components/BlickPlusOnboardingManager'
import AppPage from '@components/BlickPlusOnboardingModal/AppPage'
import Footer from '@components/BlickPlusOnboardingModal/Footer'
import Header from '@components/BlickPlusOnboardingModal/Header'
import HighlightsPage from '@components/BlickPlusOnboardingModal/HighlightsPage'
import NewsletterPage from '@components/BlickPlusOnboardingModal/NewsletterPage'
import WelcomePage from '@components/BlickPlusOnboardingModal/WelcomePage'

const ModalOverlay = styled.div`
  display: flex;
  position: fixed;
  overflow-y: scroll;

  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  justify-content: center;
  ${mobileCSS(css`
    align-items: flex-start;
  `)}
  //To hide the header
  z-index: 10000;
`

const ModalContent = styled.div`
  ${({
    theme: {
      colors: { fillDefault },
      spacing: { spacing32, spacing16, spacing24 },
    },
  }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    margin: auto 0;
    overflow-y: scroll;

    width: 632px;

    background-color: ${fillDefault};
    padding: 40px 109px;
    gap: ${spacing24};
    ${mobileCSS(css`
      padding: ${spacing32} ${spacing16};
      width: 100%;
      margin: 0;
      min-height: 100vh;
    `)}
  `}
`

const STEP_COMPONENTS: Record<Exclude<Step, 'welcome'>, ReactNode> = {
  app: <AppPage />,
  newsletter: <NewsletterPage />,
  highlights: <HighlightsPage />,
}

const BlickPlusOnboardingModal: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const { lockBodyScroll, unlockBodyScroll } = useBodyScroll()
  const isBodyScrollLocked = useRef(false)

  const utilities = queryClient.getQueryData<OnboardingUtilities>([
    'blick-plus-onboarding-utilities',
  ])

  const initialSteps = useBlickPlusOnboardingStepsABTest()
  const stepsWithoutWelcome = initialSteps?.slice(1)

  const currentStepIndex = useBlickPlusOnboardingCurrentStep()
  const currentStepIndexWithoutWelcome = currentStepIndex - 1

  const shouldBeOpen = !!initialSteps && !!initialSteps.length
  const isOpen = shouldBeOpen && currentStepIndex !== -1

  useEffect(() => {
    if (isOpen) {
      isBodyScrollLocked.current = true
      lockBodyScroll()
    } else if (isBodyScrollLocked.current) {
      isBodyScrollLocked.current = false
      unlockBodyScroll()
    }

    return () => {
      if (isBodyScrollLocked.current) {
        unlockBodyScroll()
      }
    }
  }, [isOpen, lockBodyScroll, unlockBodyScroll])

  if (!stepsWithoutWelcome || !utilities) {
    return null
  }

  const currentStep = stepsWithoutWelcome[
    currentStepIndexWithoutWelcome
  ] as Exclude<Step, 'welcome'>

  return isOpen ? (
    <ModalOverlay>
      {currentStepIndex === 0 ? (
        <WelcomePage
          onClose={utilities.onClose}
          onClickNext={utilities.onNext}
        />
      ) : (
        <ModalContent>
          <Header
            onClose={utilities.onClose}
            steps={stepsWithoutWelcome}
            currentStepIndex={currentStepIndexWithoutWelcome}
          />
          {STEP_COMPONENTS[currentStep]}
          <Footer
            onClickNext={utilities.onNext}
            onClose={utilities.onClose}
            isLastStep={
              currentStepIndexWithoutWelcome === stepsWithoutWelcome.length - 1
            }
          />
        </ModalContent>
      )}
    </ModalOverlay>
  ) : null
}

export default BlickPlusOnboardingModal
